import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Modal, Button, Alert } from 'react-bootstrap';
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import logo from '../logo.png';

const ARGENTINA_TIMEZONE = 'America/Argentina/Buenos_Aires';

const Login = () => {
    const userRef = useRef();
    const [username, setUserDNI] = useState('');
    const [name, setName] = useState('');
    const [userId, setUserId] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [trainingWeek, setTrainingWeek] = useState(1);
    const [hasExpiredQuota, setHasExpiredQuota] = useState(false);
    const [lastPaymentDate, setLastPaymentDate] = useState(null);
    const [quotaValidUntil, setQuotaValidUntil] = useState(null);
    const [sessionProgress, setSessionProgress] = useState(null);

    useEffect(() => {
        userRef.current.focus();
    }, []);

    const checkQuotaExpired = (membershipExpiresAt, lastPayment) => {
        if (!membershipExpiresAt && !lastPayment) return true;

        const validUntilDate = membershipExpiresAt
            ? new Date(`${String(membershipExpiresAt).slice(0, 10)}T00:00:00`)
            : new Date(lastPayment);
        const today = new Date();

        if (!membershipExpiresAt) {
            validUntilDate.setDate(validUntilDate.getDate() + 30);
        }

        // Resetear las horas para comparar solo fechas
        validUntilDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        // Guardar la fecha hasta cuando está válida la cuota
        setQuotaValidUntil(validUntilDate);

        // La fecha de vencimiento es el primer día sin cobertura.
        return validUntilDate <= today;
    };

    const getSessionProgress = async (user) => {
        const contractedSessions = Number(user.sessionsWeek);

        try {
            const response = await axios.get('https://iron.nextriking.com/api/sessions/stats');
            const sessions = response.data.body || [];
            const today = moment().tz(ARGENTINA_TIMEZONE);
            const weekStart = moment().tz(ARGENTINA_TIMEZONE).startOf('week');
            const userSessions = sessions.filter((session) => Number(session.userId) === Number(user.id));
            const sessionsThisWeek = userSessions.filter((session) =>
                moment(session.login).tz(ARGENTINA_TIMEZONE).isSameOrAfter(weekStart)
            ).length;

            // La asistencia actual se registra al confirmar, pero debe mostrarse
            // como la sesión que el socio está por realizar.
            return {
                current: contractedSessions ? sessionsThisWeek + 1 : null,
                contracted: contractedSessions || null,
                hasSessionToday: userSessions.some((session) =>
                    moment(session.login).tz(ARGENTINA_TIMEZONE).isSame(today, 'day')
                )
            };
        } catch (error) {
            console.error('Error al cargar las sesiones semanales:', error);
            return null;
        }
    };

    const getTrainingWeek = (user) => {
        const registrationDate = user.firstPayment || user.createdAt || user.created_at;
        const startDate = moment.tz(registrationDate, ARGENTINA_TIMEZONE).startOf('day');

        if (!startDate.isValid()) return 1;

        const elapsedWeeks = Math.max(0, moment().tz(ARGENTINA_TIMEZONE).startOf('day').diff(startDate, 'weeks'));
        return (elapsedWeeks % 14) + 1;
    };

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        setError(null);
        setSuccessMessage('');
        setHasExpiredQuota(false);
        setQuotaValidUntil(null);
        setSessionProgress(null);

        try {
            const response = await axios.get(`https://iron.nextriking.com/api/users/email/${username}`);

            if (response.data.error === false && response.data.body.length > 0) {
                const user = response.data.body[0];
                setUserId(user.id);
                setName(user.name);
                setLastPaymentDate(user.lastPayment);
                setTrainingWeek(getTrainingWeek(user));

                const isExpired = checkQuotaExpired(user.membership_expires_at, user.lastPayment);
                setHasExpiredQuota(isExpired);
                const progress = await getSessionProgress(user);

                if (progress?.hasSessionToday) {
                    setError('Ya iniciaste sesión hoy.');
                    return;
                }

                setSessionProgress(progress);

                setSuccessMessage("Usuario encontrado. Completa el formulario.");
                setShowModal(true);
            } else {
                setError('Usuario no encontrado.');
            }
        } catch (error) {
            setError('Error al iniciar sesión. Inténtalo de nuevo más tarde.');
            console.error('Error en inicio de sesión:', error);
        }
    }, [username]);

    const handleFormSubmit = useCallback(async (event) => {
        event.preventDefault();

        try {
            const response = await enviarDatosSesion(userId, trainingWeek);

            if (response) {
                Swal.fire({
                    title: "¡Éxito!",
                    text: response.message,
                    icon: "success",
                    confirmButtonText: "OK",
                    timer: 4000,
                    timerProgressBar: true,
                    showConfirmButton: true,
                    willClose: () => {
                        console.log('Diálogo cerrándose');
                    }
                }).then((result) => {
                    console.log('Resultado:', result);

                    setShowModal(false);
                    setSuccessMessage("");
                    setName("")
                    setUserId('')
                    setUserDNI('')
                    setTrainingWeek(1)
                    setHasExpiredQuota(false)
                    setLastPaymentDate(null)
                    setQuotaValidUntil(null)
                    setSessionProgress(null)
                });
            }
        } catch (error) {
            Swal.fire({
                title: "Error",
                text: "Hubo un problema, reintenta luego.",
                icon: "error",
                confirmButtonText: "Intentar de nuevo"
            });
        }
    }, [userId, trainingWeek]);

    const enviarDatosSesion = async (userId, trainingWeek) => {
        try {
            const payload = {
                userId,
                establishmentId: 1,
                trainingWeek,
                todayRoutine: 'Asistencia general',
            };

            // The API stores this instant; send the Buenos Aires offset explicitly.
            payload.login = moment().tz(ARGENTINA_TIMEZONE).format();
            payload.timezone = ARGENTINA_TIMEZONE;

            console.log("Enviando payload:", payload);

            const response = await axios.post(
                "https://iron.nextriking.com/api/sessions/in",
                payload,
                { headers: { "Content-Type": "application/json" } }
            );

            console.log("Respuesta del servidor:", response.data);
            return response.data;
        } catch (error) {
            console.error("Error al enviar los datos:", error.response?.data || error.message);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Sin registro';
        return moment(dateString).tz(ARGENTINA_TIMEZONE).format('DD/MM/YYYY');
    };

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="card p-4 text-center" style={{ width: "320px" }}>
                    <img src={logo} alt="Iron muscle Logo" className="mb-3 mx-auto d-block" style={{ width: "100px" }} />
                    <h3 className="mb-3">Bienvenido a Iron muscle</h3>
                    <form onSubmit={handleSubmit}>
                        <input
                            type="text"
                            ref={userRef}
                            value={username}
                            onChange={(e) => setUserDNI(e.target.value)}
                            placeholder="Ingresa tu DNI"
                            className="form-control mb-2"
                        />
                        <button type="submit" className="btn btn-primary w-100">Buscar usuario</button>
                        {error && <p className="text-danger mt-2">{error}</p>}
                        {successMessage && <p className="text-success mt-2">{successMessage}</p>}
                    </form>
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{name}, confirmar asistencia</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {sessionProgress?.current != null && (
                        <Alert variant={sessionProgress.current >= sessionProgress.contracted ? 'warning' : 'info'} className="mb-3">
                            <strong>Asistencias esta semana: {sessionProgress.current} de {sessionProgress.contracted}</strong>
                        </Alert>
                    )}
                    <Alert variant="light" className="mb-3 border">
                        <strong>Último pago:</strong> {formatDate(lastPaymentDate)}
                    </Alert>
                    {/* Mensaje de cuota vencida */}
                    {hasExpiredQuota && (
                        <Alert variant="danger" className="mb-3">
                            <Alert.Heading className="d-flex align-items-center">
                                <span style={{ fontSize: '1.5rem', marginRight: '8px' }}>⚠️</span>
                                Cuota Vencida
                            </Alert.Heading>
                            <p className="mb-1">
                                <strong>Tu cuota de pago está vencida.</strong>
                            </p>
                            <p className="mb-1">
                                Último pago: <strong>{formatDate(lastPaymentDate)}</strong>
                            </p>
                            <p className="mb-2">
                                Vencimiento: <strong>{formatDate(quotaValidUntil)}</strong>
                            </p>
                            <hr />
                            <p className="mb-0" style={{ fontSize: '0.95rem' }}>
                                Por favor, acércate al mostrador para regularizar tu situación antes de entrenar.
                            </p>
                        </Alert>
                    )}

                    <form onSubmit={handleFormSubmit}>
                        <Button type="submit" variant="success">Enviar</Button>
                    </form>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default Login;
